import { motion, useTransform, useScroll } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { gsap } from "gsap";
import IMG_0387 from "../imgs/IMG_0387.JPEG";
import IMG_0379 from "../imgs/IMG_0379.JPEG";
import IMG_0372 from "../imgs/IMG_0372.JPEG";
import IMG_0375 from "../imgs/IMG_0375.JPEG";
import IMG_0370 from "../imgs/IMG_0370.JPEG";
import art1 from "../svg/art1.svg";
import art2 from "../svg/art2.svg";
import art3 from "../svg/art3.svg";
import art4 from "../svg/art4.svg";
import art5 from "../svg/art5.svg";

const RaveSection = () => {
  const targetRef = useRef(null);
  const carouselRef = useRef(null);
  const [maxTranslate, setMaxTranslate] = useState(0);
  const { scrollYProgress } = useScroll({
    target: targetRef,
  });

  // Calculate the maximum translate value based on carousel width
  useEffect(() => {
    const calculateMaxTranslate = () => {
      if (carouselRef.current) {
        const carouselWidth = carouselRef.current.scrollWidth;
        const viewportWidth = window.innerWidth;
        // Calculate how much we need to move: (carousel width - viewport width)
        setMaxTranslate(-(carouselWidth - viewportWidth));
      }
    };

    calculateMaxTranslate();
    window.addEventListener("resize", calculateMaxTranslate);

    // Recalculate after images load
    const timer = setTimeout(calculateMaxTranslate, 100);

    return () => {
      window.removeEventListener("resize", calculateMaxTranslate);
      clearTimeout(timer);
    };
  }, []);

  // Transform to allow continuous scrolling past the last image
  const x = useTransform(scrollYProgress, [0, 1], [0, maxTranslate]);

  const images = [
    { img: IMG_0387, art: art1 },
    { img: IMG_0379, art: art2 },
    { img: IMG_0372, art: art3 },
    { img: IMG_0375, art: art5 },
    { img: IMG_0370, art: art4 },
  ];

  return (
    <section ref={targetRef} className="relative h-[200vh] bg-black">
      <div className="sticky top-0 flex flex-col h-screen overflow-hidden justify-center">
        <div className="bg-black text-white pt-10 pb-6 md:pt-0 md:pb-12 flex items-center justify-center flex-shrink-0 z-10">
          <div className="font-bold md:text-[70px] text-[40px] font-Neuehaasdisplay md:leading-[65px] text-center whitespace-nowrap">
            O<span className="font-dirtyline">N</span>E&nbsp;N
            <span className="font-dirtyline">I</span>G
            <span className="font-dirtyline">H</span>T&nbsp;W
            <span className="font-dirtyline">I</span>T
            <span className="font-dirtyline">H</span>&nbsp;U
            <span className="font-dirtyline">S</span>
          </div>
        </div>
        {/* Added pl-4 to give the first large image a little starting gap on mobile */}
        {/* Added padding to accommodate stamps that extend outside */}
        <div className="flex w-full items-center overflow-hidden pl-4 md:pl-0 pt-6 pr-6">
          <motion.div
            ref={carouselRef}
            style={{ x }}
            className="flex gap-4 sm:gap-4 md:gap-6"
          >
            {images.map((item, index) => (
              <Card key={index} index={index} image={item.img} art={item.art} />
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Card = ({ image, art, index }) => {
  // Rotation logic: alternate rotation for each stamp
  const baseRotation = index % 2 === 0 ? 12 : -6; // Base rotation in degrees
  const rotationClass = index % 2 === 0 ? "rotate-12" : "-rotate-6";
  const stampRef = useRef(null);

  // GSAP wind flap animation - makes stamp look like a loose sticker flapping in wind
  useEffect(() => {
    if (stampRef.current) {
      // Set transform origin to bottom-left corner for natural flap motion
      gsap.set(stampRef.current, {
        transformOrigin: "bottom left",
        rotation: baseRotation, // Set initial rotation
      });

      // Create wind flap animation timeline
      const tl = gsap.timeline({
        repeat: -1,
        delay: index * 0.3, // Stagger animations for natural variation
      });

      // Flap motion: rotate and translate to simulate wind
      tl.to(stampRef.current, {
        rotation: baseRotation + (index % 2 === 0 ? 6 : -3), // Slight rotation variation
        x: 3,
        y: -2,
        duration: 1.2 + Math.random() * 0.4, // Random duration for natural feel
        ease: "sine.inOut",
      })
        .to(stampRef.current, {
          rotation: baseRotation + (index % 2 === 0 ? -6 : 3), // Return with variation
          x: -2,
          y: 1,
          duration: 1.1 + Math.random() * 0.3,
          ease: "sine.inOut",
        })
        .to(stampRef.current, {
          rotation: baseRotation, // Back to base
          x: 0,
          y: 0,
          duration: 1.0 + Math.random() * 0.2,
          ease: "sine.inOut",
        });
    }
  }, [index, baseRotation]);

  return (
    /* UPDATED DIMENSIONS FOR MOBILE:
       1. h-[60vh]: Takes up 60% of the screen height (much taller than before).
       2. w-[85vw]: Takes up 85% of the screen width. This leaves a 15% peek 
          for the next image, encouraging the user to scroll.
       3. sm:... : At 'sm' breakpoint, we revert to the fixed pixel sizes for tablets/desktops.
    */
    <div className="group relative h-[60vh] w-[85vw] sm:h-[300px] sm:w-[300px] md:h-[500px] md:w-[400px] lg:h-[600px] lg:w-[500px] xl:h-[700px] xl:w-[600px] flex-shrink-0 shadow-2xl overflow-visible">
      {/* Layer A: Image container with overflow-hidden for zoom effect */}
      <div className="relative h-full w-full overflow-hidden bg-neutral-200">
        <div
          style={{
            backgroundImage: `url(${image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          className="absolute inset-0 z-0 transition-transform duration-300 group-hover:scale-110"
        ></div>
      </div>
      {/* Layer B: SVG Stamp - outside Layer A with negative positioning */}
      <div
        ref={stampRef}
        className={`absolute -top-6 -right-6 z-50 ${rotationClass} pointer-events-none`}
      >
        <img
          src={art}
          alt="art"
          className="w-28 h-28 sm:w-24 sm:h-24 md:w-32 md:h-32 lg:w-40 lg:h-40 drop-shadow-xl"
        />
      </div>
    </div>
  );
};

export default RaveSection;
